import { useState } from "react";
import { toast } from "sonner";
import { Plus, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Panel, StatusBadge, Sel, Field, Spinner, DataTable, opts } from "@/components/kit";
import { api, useApi, fmtErr, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SLOT_TYPES = ["regular", "motorcycle", "pwd", "vip", "reserved", "tenant", "staff", "loading", "blocked"];
const SLOT_ST = ["available", "reserved", "blocked", "out_of_service"];
const SLOT_COLOR = { available: "bg-emerald-500/20 border-emerald-500/50", occupied: "bg-cyan-500/30 border-cyan-500", reserved: "bg-amber-500/20 border-amber-500/50", blocked: "bg-red-500/20 border-red-500/50", out_of_service: "bg-slate-500/30 border-slate-500" };

function BranchForm({ init, onClose, onSaved }) {
  const [f, setF] = useState(init);
  const save = async () => {
    try {
      f.id ? await api.put(`/branches/${f.id}`, f) : await api.post("/branches", f);
      toast.success("Branch saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card" data-testid="branch-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit branch" : "New branch"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" className="col-span-2"><Input data-testid="branch-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Code"><Input data-testid="branch-code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} /></Field>
          <Field label="Operating hours"><Input data-testid="branch-hours" value={f.operating_hours || ""} onChange={(e) => setF({ ...f, operating_hours: e.target.value })} /></Field>
          <Field label="Address" className="col-span-2"><Input data-testid="branch-address" value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
          <Field label="City"><Input data-testid="branch-city" value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
          <Field label="TIN"><Input data-testid="branch-tin" value={f.tin || ""} onChange={(e) => setF({ ...f, tin: e.target.value })} /></Field>
        </div>
        <Button data-testid="branch-save" onClick={save} disabled={!f.name || !f.code}>Save</Button>
      </DialogContent>
    </Dialog>
  );
}

function SlotEdit({ slot, onClose, onSaved }) {
  const [f, setF] = useState({ type: slot.type, status: slot.status === "occupied" ? "" : slot.status });
  const save = async () => {
    try {
      await api.put(`/slots/${slot.id}`, f);
      toast.success("Slot updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card" data-testid="slot-dialog">
        <DialogHeader><DialogTitle>Slot {slot.code}</DialogTitle></DialogHeader>
        {slot.plate && <div className="text-sm">Occupied by <span className="plate">{slot.plate}</span></div>}
        <Field label="Category"><Sel value={f.type} onChange={(v) => setF({ ...f, type: v })} options={opts(SLOT_TYPES)} testid="slot-type" className="w-full" /></Field>
        {slot.status !== "occupied" && <Field label="Status"><Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} options={opts(SLOT_ST)} testid="slot-status" className="w-full" /></Field>}
        <Button data-testid="slot-save" onClick={save}>Save</Button>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ id, onClose }) {
  const { can } = useAuth();
  const { data, reload } = useApi(id ? `/branches/${id}` : null, {});
  const rates = useApi(id ? "/rates" : null, { branch_id: id });
  const [slot, setSlot] = useState(null);
  const [zone, setZone] = useState({ name: "", floor: "G", slot_count: 10, slot_type: "regular" });
  const [lane, setLane] = useState({ name: "", direction: "entry" });
  const post = async (url, body, msg) => {
    try {
      await api.post(url, body);
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open={!!id} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto bg-card" data-testid="branch-detail-dialog">
        <DialogHeader><DialogTitle>{data ? `${data.code} — ${data.name}` : "Branch"}</DialogTitle></DialogHeader>
        {!data ? <Spinner /> : (
          <Tabs defaultValue="slots">
            <TabsList className="flex-wrap">
              <TabsTrigger value="slots" data-testid="branch-tab-slots">Slots & zones</TabsTrigger>
              <TabsTrigger value="lanes" data-testid="branch-tab-lanes">Lanes & devices</TabsTrigger>
              <TabsTrigger value="rates" data-testid="branch-tab-rates">Rates</TabsTrigger>
              <TabsTrigger value="staff" data-testid="branch-tab-staff">Staff</TabsTrigger>
            </TabsList>
            <TabsContent value="slots" className="mt-4 space-y-4">
              <div className="flex flex-wrap gap-2 text-[11px]">{Object.entries(SLOT_COLOR).map(([k, c]) => <span key={k} className={`rounded border px-2 py-0.5 ${c}`}>{label(k)}</span>)}</div>
              {data.zones.map((z) => {
                const ss = data.slots.filter((s) => s.zone_id === z.id);
                return (
                  <div key={z.id}>
                    <div className="mb-1.5 text-sm font-semibold">{z.name} <span className="text-xs text-muted-foreground">Floor {z.floor} • {ss.filter((s) => s.status === "occupied").length}/{ss.length} occupied</span></div>
                    <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-10 lg:grid-cols-14" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(64px,1fr))" }}>
                      {ss.map((s) => (
                        <button key={s.id} data-testid={`slot-${s.code}`} onClick={() => can("edit") && setSlot(s)} title={`${s.code} ${label(s.type)} ${s.plate || ""}`}
                          className={`rounded border px-1 py-1.5 text-center font-mono text-[10px] leading-tight transition-transform hover:scale-105 ${SLOT_COLOR[s.status]}`}>
                          <div className="font-bold">{s.code}</div><div className="truncate opacity-75">{s.plate || label(s.type)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              {can("manage_branches") && (
                <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed p-3">
                  <Field label="New zone"><Input data-testid="zone-name" className="w-36" value={zone.name} onChange={(e) => setZone({ ...zone, name: e.target.value })} /></Field>
                  <Field label="Floor"><Input data-testid="zone-floor" className="w-16" value={zone.floor} onChange={(e) => setZone({ ...zone, floor: e.target.value })} /></Field>
                  <Field label="Slots"><Input data-testid="zone-count" type="number" className="w-20" value={zone.slot_count} onChange={(e) => setZone({ ...zone, slot_count: Number(e.target.value) })} /></Field>
                  <Field label="Type"><Sel value={zone.slot_type} onChange={(v) => setZone({ ...zone, slot_type: v })} options={opts(SLOT_TYPES)} testid="zone-type" className="w-32" /></Field>
                  <Button data-testid="zone-add" disabled={!zone.name} onClick={() => post(`/branches/${id}/zones`, zone, "Zone created")}>Add zone</Button>
                </div>
              )}
            </TabsContent>
            <TabsContent value="lanes" className="mt-4 space-y-4">
              <div className="grid gap-2 sm:grid-cols-2">{data.lanes.map((l) => (
                <div key={l.id} className="rounded-md border p-3 text-sm"><b>{l.name}</b> <StatusBadge status={l.direction} tone={l.direction === "entry" ? "cyan" : "amber"} />
                  <div className="mt-1 text-xs text-muted-foreground">{data.devices.filter((d) => d.lane_id === l.id).map((d) => d.device_code).join(" • ")}</div></div>
              ))}</div>
              {can("manage_branches") && (
                <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed p-3">
                  <Field label="New lane"><Input data-testid="lane-name" value={lane.name} onChange={(e) => setLane({ ...lane, name: e.target.value })} /></Field>
                  <Field label="Direction"><Sel value={lane.direction} onChange={(v) => setLane({ ...lane, direction: v })} options={opts(["entry", "exit"])} testid="lane-direction" className="w-32" /></Field>
                  <Button data-testid="lane-add" disabled={!lane.name} onClick={() => post(`/branches/${id}/lanes`, lane, "Lane + barrier created")}>Add lane</Button>
                </div>
              )}
              <DataTable testid="branch-devices-table" rows={data.devices} columns={[{ key: "device_code", label: "Device" }, { key: "type", label: "Type", render: (r) => label(r.type) }, { key: "lane_name", label: "Lane" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> }]} />
            </TabsContent>
            <TabsContent value="rates" className="mt-4">
              <DataTable testid="branch-rates-table" rows={rates.data} columns={[
                { key: "name", label: "Plan" }, { key: "category", label: "Category", render: (r) => label(r.category) }, { key: "vehicle_type", label: "Vehicle" },
                { key: "r", label: "1st hr / succ.", render: (r) => `₱${r.first_hour_rate} / ₱${r.succeeding_hour_rate}` }, { key: "flat_rate", label: "Flat" },
                { key: "daily_max", label: "Daily max" }, { key: "overnight_rate", label: "Overnight" }, { key: "grace_minutes", label: "Grace" },
                { key: "lost_ticket_penalty", label: "Lost ticket" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> }]} />
            </TabsContent>
            <TabsContent value="staff" className="mt-4">
              <div className="mb-2 text-sm">Manager: <b>{data.manager_name || "—"}</b></div>
              <DataTable testid="branch-staff-table" rows={data.staff} columns={[{ key: "name", label: "Name" }, { key: "role_label", label: "Role" }, { key: "email", label: "Email" }]} />
            </TabsContent>
          </Tabs>
        )}
        {slot && <SlotEdit slot={slot} onClose={() => setSlot(null)} onSaved={reload} />}
      </DialogContent>
    </Dialog>
  );
}

export default function Branches() {
  const { branches, can, reloadBranches } = useAuth();
  const [edit, setEdit] = useState(null);
  const [sel, setSel] = useState(null);
  return (
    <div className="fade-up">
      <PageHeader title="Branches" subtitle={`${branches.length} parking branch(es)`} testid="branches-header">
        {can("manage_branches") && <Button data-testid="branch-add-btn" onClick={() => setEdit({ name: "", code: "", address: "", operating_hours: "24/7" })}><Plus className="h-4 w-4" /> Branch</Button>}
      </PageHeader>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 stagger">
        {branches.map((b) => {
          const p = b.slots_total ? Math.round((b.slots_occupied * 100) / b.slots_total) : 0;
          return (
            <Panel key={b.id} testid={`branch-card-${b.code}`} title={<span className="flex items-center gap-2"><span className="rounded bg-amber-500 px-1.5 font-mono text-xs font-black text-slate-950">{b.code}</span>{b.name}</span>}
              action={can("manage_branches") && <button className="text-xs text-primary" data-testid={`branch-edit-${b.code}`} onClick={() => setEdit(b)}>Edit</button>}>
              <div className="space-y-1.5 text-sm text-muted-foreground">
                <div className="flex gap-2"><MapPin className="h-4 w-4 shrink-0" />{b.address}</div>
                <div className="flex gap-2"><Clock className="h-4 w-4" />{b.operating_hours}</div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div><div className="font-mono text-xl font-bold">{b.slots_total}</div><div className="text-[10px] uppercase text-muted-foreground">Slots</div></div>
                <div><div className="font-mono text-xl font-bold text-cyan-500">{p}%</div><div className="text-[10px] uppercase text-muted-foreground">Occupied</div></div>
                <div><div className="font-mono text-xl font-bold">{b.lanes.length}</div><div className="text-[10px] uppercase text-muted-foreground">Lanes</div></div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">{b.zones.length} zones • {b.lanes.filter((l) => l.direction === "entry").length} entry / {b.lanes.filter((l) => l.direction === "exit").length} exit</div>
              <Button variant="outline" size="sm" className="mt-3 w-full" data-testid={`branch-open-${b.code}`} onClick={() => setSel(b.id)}>Open configuration</Button>
            </Panel>
          );
        })}
      </div>
      {edit && <BranchForm init={edit} onClose={() => setEdit(null)} onSaved={reloadBranches} />}
      <Detail id={sel} onClose={() => { setSel(null); reloadBranches(); }} />
    </div>
  );
}
